// frontend/src/utils/pushNotificationHandler.js
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import API_BASE from "../config/api";
import { onForegroundMessage } from "../firebase";

/**
 * Initialize Push Notifications across Android (Capacitor) and Web
 * @param {Function} navigate - React Router navigate function
 */
export const initPushNotifications = async (navigate) => {
  // ================= 1. NATIVE ANDROID (CAPACITOR) =================
  if (Capacitor.isNativePlatform()) {
    try {
      console.log("📱 Initializing Native Capacitor Push Notifications...");

      // 1. Create notification channel (Android 8.0+)
      try {
        await PushNotifications.createChannel({
          id: "gj_bullion_rates",
          name: "GJ Bullion Rate Alerts",
          description: "Live physical bullion rates and trade updates",
          importance: 5, // High importance (heads-up banner)
          visibility: 1, // Public on lockscreen
          sound: "default",
          vibration: true,
          lights: true,
          lightColor: "#f5d76e",
        });
      } catch (channelErr) {
        console.warn("Channel setup notice:", channelErr?.message || channelErr);
      }

      // 2. Install registration listeners BEFORE calling register()
      PushNotifications.addListener("registration", async (token) => {
        const masked = token.value ? `${token.value.slice(0, 10)}...${token.value.slice(-6)}` : "empty";
        console.log("✅ Native FCM Registration Token captured safely:", masked);
        localStorage.setItem("gj_fcm_token", token.value);

        const user = JSON.parse(localStorage.getItem("user") || "null");

        // Sync with backend
        try {
          await fetch(`${API_BASE}/api/notifications/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              token: token.value,
              platform: "android",
              userId: user?._id || user?.id || null,
              preferences: {
                rateAlerts: true,
                promotions: false, // Strictly default false (explicit opt-in required)
                orderUpdates: true,
              },
              deviceInfo: {
                platform: Capacitor.getPlatform(),
                userAgent: navigator.userAgent,
              },
            }),
          });
          console.log("✅ Android token synced with GJ Bullion backend");
        } catch (syncErr) {
          console.error("❌ Failed to sync Android token to backend:", syncErr?.message || syncErr);
        }
      });

      // 3. Registration error listener
      PushNotifications.addListener("registrationError", (err) => {
        console.error("❌ Native Push Registration Error:", err?.error || err);
      });

      // 4. Foreground notification received (show in-app toast for active screen)
      PushNotifications.addListener("pushNotificationReceived", (notification) => {
        console.log("🔔 Foreground push received:", notification);
        const title = notification.title || "GJ Bullion Alert";
        const body = notification.body || "";
        const imageUrl = notification.data?.imageUrl || "";
        const destination = notification.data?.destination || "/#rates";
        showInAppForegroundNotification({ title, body, imageUrl, destination, navigate });
      });

      // 5. Notification Click Action Performed (Background/Terminated App Click)
      PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        console.log("👆 Notification tapped:", action);
        const data = action.notification?.data;
        const targetUrl = data?.destination || data?.url || "/#rates";

        if (navigate && targetUrl) {
          if (targetUrl.startsWith("/#")) {
            window.location.hash = targetUrl.replace("/#", "");
          } else {
            navigate(targetUrl);
          }
        }
      });

      // 6. Request Permission & Trigger Registration
      let permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive === "prompt") {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== "granted") {
        console.warn("⚠️ Native notification permission not granted:", permStatus.receive);
        return;
      }

      // 7. Register with Google / Apple to receive push token
      await PushNotifications.register();
    } catch (err) {
      console.error("❌ Capacitor Push Initialization Error:", err?.message || err);
    }
  }

  // ================= 2. WEB BROWSER =================
  else {
    try {
      // Listen for foreground notifications on Web
      onForegroundMessage((payload) => {
        console.log("🌐 Web Foreground notification:", payload);
        const title = payload.notification?.title || payload.data?.title || "GJ Bullion Alert";
        const body = payload.notification?.body || payload.data?.body || "";
        const imageUrl = payload.notification?.image || payload.data?.imageUrl || "";
        const destination = payload.data?.destination || payload.data?.url || "/#rates";

        // A. Native browser notification only when tab is hidden / minimized in background
        if (
          document.visibilityState === "hidden" &&
          typeof window !== "undefined" &&
          "Notification" in window &&
          Notification.permission === "granted"
        ) {
          try {
            const notif = new Notification(title, {
              body,
              icon: "/assets/logo.jpeg",
              image: imageUrl || undefined,
              tag: payload.data?.idempotencyKey || payload.data?.campaignId || "gj-foreground-alert",
              data: { destination },
            });
            notif.onclick = () => {
              window.focus();
              if (destination) {
                if (destination.startsWith("/#")) {
                  window.location.hash = destination.replace("/#", "");
                } else if (navigate) {
                  navigate(destination);
                }
              }
            };
          } catch (e) {
            console.warn("Native Notification display skipped:", e);
          }
        }

        // B. Clear visual in-app banner feedback (always displayed when active tab receives message)
        showInAppForegroundNotification({ title, body, imageUrl, destination, navigate });
      });
    } catch (webErr) {
      console.warn("Web foreground listener not initialized:", webErr);
    }
  }
};

/**
 * Renders an in-app foreground notification toast for immediate visual feedback
 */
const showInAppForegroundNotification = ({ title, body, imageUrl, destination, navigate }) => {
  if (typeof document === "undefined") return;

  const existing = document.getElementById("gj-foreground-toast");
  if (existing) existing.remove();

  const toast = document.createElement("div");
  toast.id = "gj-foreground-toast";
  toast.style.position = "fixed";
  toast.style.top = "20px";
  toast.style.right = "20px";
  toast.style.zIndex = "9999999";
  toast.style.maxWidth = "360px";
  toast.style.width = "calc(100% - 40px)";
  toast.style.background = "linear-gradient(135deg, #18181b 0%, #09090b 100%)";
  toast.style.border = "1.5px solid #d4af37";
  toast.style.boxShadow = "0 10px 25px -5px rgba(0, 0, 0, 0.7), 0 0 15px rgba(212, 175, 55, 0.3)";
  toast.style.borderRadius = "12px";
  toast.style.padding = "14px";
  toast.style.display = "flex";
  toast.style.flexDirection = "column";
  toast.style.gap = "8px";
  toast.style.color = "#f4f4f5";
  toast.style.fontFamily = "system-ui, -apple-system, sans-serif";
  toast.style.cursor = "pointer";

  const imageHtml = imageUrl
    ? `<img src="${imageUrl}" style="width:100%;max-height:160px;object-fit:cover;border-radius:6px;margin-top:4px;" alt="preview" onerror="this.style.display='none'" />`
    : "";

  toast.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;width:100%;">
      <div style="display:flex;align-items:center;gap:8px;">
        <img src="/assets/logo.jpeg" alt="GJ" style="width:22px;height:22px;border-radius:4px;object-fit:cover;" />
        <span style="font-weight:700;font-size:0.75rem;letter-spacing:0.5px;color:#f5d76e;text-transform:uppercase;">GJ BULLION ALERT</span>
      </div>
      <button id="gj-toast-close" style="background:none;border:none;color:#a1a1aa;font-size:16px;cursor:pointer;padding:0 4px;">✕</button>
    </div>
    <div style="font-weight:700;font-size:0.92rem;color:#ffffff;line-height:1.3;">${title}</div>
    <div style="font-size:0.82rem;color:#d4d4d8;line-height:1.4;">${body}</div>
    ${imageHtml}
  `;

  document.body.appendChild(toast);

  const handleClick = (e) => {
    if (e.target.id === "gj-toast-close") {
      toast.remove();
      return;
    }
    toast.remove();
    if (destination) {
      if (destination.startsWith("/#")) {
        window.location.hash = destination.replace("/#", "");
      } else if (navigate) {
        navigate(destination);
      }
    }
  };

  toast.addEventListener("click", handleClick);

  setTimeout(() => {
    if (document.body.contains(toast)) {
      toast.style.opacity = "0";
      toast.style.transition = "opacity 0.4s ease-out";
      setTimeout(() => toast.remove(), 400);
    }
  }, 7000);
};
